import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  CreateAppointmentRequest,
  DashboardAppointment,
  DashboardAppointmentsResponse,
  UpdateAppointmentRequest,
} from "@/api/types";
import {
  businessSlugFromOverview,
  dashboardQueryKeyForSlug,
  useDashboardRequest,
} from "./dashboard-request";

export function useDashboardAppointments(startDate: string, endDate: string) {
  const dashboard = useDashboardRequest();

  return useQuery({
    queryKey: dashboard.queryKey("appointments", { startDate, endDate }),
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      api
        .get("dashboard/appointments", {
          ...dashboard.requestOptions(),
          searchParams: { start_date: startDate, end_date: endDate },
        })
        .json<DashboardAppointmentsResponse>(),
    placeholderData: (prev) => prev,
  });
}

function invalidateAppointmentQueries(
  queryClient: QueryClient,
  appointmentId: string,
  requestSlug: string | undefined,
) {
  const slug = requestSlug ?? businessSlugFromOverview(queryClient);
  void queryClient.invalidateQueries({ queryKey: dashboardQueryKeyForSlug(requestSlug) });
  if (slug && slug !== requestSlug) {
    void queryClient.invalidateQueries({ queryKey: dashboardQueryKeyForSlug(slug) });
  }
  void queryClient.invalidateQueries({ queryKey: ["bookings"] });
  void queryClient.invalidateQueries({ queryKey: ["booking", appointmentId] });
  void queryClient.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === "businesses" &&
      queryKey[2] === "availability" &&
      (!slug || queryKey[1] === slug),
  });
}

export function useCreateAppointment() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (data: CreateAppointmentRequest) =>
      api
        .post("dashboard/appointments", { ...dashboard.requestOptions(), json: data })
        .json<DashboardAppointment>(),
    onSuccess: (appointment) =>
      invalidateAppointmentQueries(queryClient, appointment.id, dashboard.slug),
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateAppointmentRequest) =>
      api
        .patch(`dashboard/appointments/${id}`, { ...dashboard.requestOptions(), json: data })
        .json<DashboardAppointment>(),
    onSuccess: (appointment) =>
      invalidateAppointmentQueries(queryClient, appointment.id, dashboard.slug),
  });
}

export function useCancelAppointment() {
  const queryClient = useQueryClient();
  const dashboard = useDashboardRequest();

  return useMutation({
    mutationFn: (id: string) =>
      api
        .delete(`dashboard/appointments/${id}`, dashboard.requestOptions())
        .json<DashboardAppointment>(),
    onSuccess: (appointment) =>
      invalidateAppointmentQueries(queryClient, appointment.id, dashboard.slug),
  });
}
